import * as THREE from 'three'
import type { FileInfo } from './file-info'
import type { EntityHighlightTarget } from '../types/entity-highlight-target'
import { ImportedModelObjectMetadataService } from '../metadata'
import { createImportedBodyOwnershipLookupKeys } from '../utils'
import { inheritStrippedRootUserData } from './stripped-root-user-data'
import { asRecord } from './utils'
import {
  readNumberMetadataValue,
  readStringMetadataValue,
} from './metadata-reader'
import type { ImportedBodyEntry, ImportedSemanticKind } from './glb'
import type { LoaderMetadata, Matrix4Tuple } from './types'

/**
 * glTF primitive 到导入 body 的运行时映射。
 */
type PrimitiveEntityEntry = {
  /** primitive 在当前上下文中的索引。 */
  primitiveIndex: number
  /** 后端 body 标识。 */
  bodyId: string
  /** 展示标签。 */
  label: string
}

const EXPORTED_GLB_REFERENCE_SUFFIX_RE = /(\.glb)(?:[|_][0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/iu

/**
 * 规范化 body 高亮使用的 primitive 索引。
 *
 * @param indices 原始 primitive 索引集合。
 * @param primitiveSpan 当前 mesh 的 primitive 数量。
 * @returns 去重、排序且合法的 primitive 索引集合。
 */
function normalizeLocalPrimitiveIndices(indices: number[], primitiveSpan: number): number[] | undefined {
  const normalized = Array.from(new Set(indices))
    .filter((index) => Number.isInteger(index) && index >= 0 && index < primitiveSpan)
    .sort((previous, next) => previous - next)
  return normalized.length > 0 ? normalized : undefined
}

/**
 * 解析 mesh 中可用于高亮的 primitive 数量。
 *
 * @param mesh 目标 mesh。
 * @returns primitive 数量。
 */
function resolveMeshPrimitiveSpan(mesh: THREE.Mesh): number {
  const geometry = mesh.geometry
  if (!(geometry instanceof THREE.BufferGeometry)) {
    return 1
  }
  return geometry.groups.length > 0 ? geometry.groups.length : 1
}

/**
 * 解析 mesh 的 primitive body 上下文。
 *
 * @param mesh 目标 mesh。
 * @returns primitive 记录和相对当前 mesh 的索引偏移。
 */
function resolveMeshPrimitiveEntityContext(
  mesh: THREE.Mesh,
): { entries: PrimitiveEntityEntry[]; primitiveIndexOffset: number } {
  const ownEntries = Array.isArray(mesh.userData?.primitiveEntityEntries)
    ? mesh.userData.primitiveEntityEntries as PrimitiveEntityEntry[]
    : []
  if (ownEntries.length > 0) {
    return {
      entries: ownEntries,
      primitiveIndexOffset: 0,
    }
  }

  const parent = mesh.parent
  if (!parent) {
    return {
      entries: [],
      primitiveIndexOffset: 0,
    }
  }

  const parentEntries = Array.isArray(parent.userData?.primitiveEntityEntries)
    ? parent.userData.primitiveEntityEntries as PrimitiveEntityEntry[]
    : []
  if (parentEntries.length === 0) {
    return {
      entries: [],
      primitiveIndexOffset: 0,
    }
  }

  let primitiveIndexOffset = 0
  for (const sibling of parent.children) {
    if (sibling === mesh) {
      break
    }
    if (sibling instanceof THREE.Mesh) {
      primitiveIndexOffset += 1
    }
  }

  return {
    entries: parentEntries,
    primitiveIndexOffset,
  }
}

/**
 * 克隆 metadata，避免 manifest 与 Three.js userData 共享可变引用。
 *
 * @param value 原始 metadata。
 * @returns 浅克隆 metadata。
 */
export function cloneMetadata(value: unknown): LoaderMetadata | undefined {
  const record = asRecord(value)
  if (!record) {
    return undefined
  }
  return { ...record }
}

/**
 * 读取字符串字段。
 *
 * @param records 候选记录集合。
 * @param keys 候选键名集合。
 * @returns 第一个有效字符串。
 */
export function readStringValue(records: Record<string, unknown>[], keys: string[]): string | undefined {
  return readStringMetadataValue(records, keys)
}

/**
 * 读取布尔字段。
 *
 * @param records 候选记录集合。
 * @param keys 候选键名集合。
 * @returns 第一个有效布尔值。
 */
export function readBooleanValue(records: Record<string, unknown>[], keys: string[]): boolean | undefined {
  for (const record of records) {
    for (const key of keys) {
      const raw = record[key]
      if (typeof raw === 'boolean') {
        return raw
      }
      if (typeof raw === 'number') {
        if (raw === 1) return true
        if (raw === 0) return false
      }
      if (typeof raw === 'string') {
        const normalized = raw.trim().toLowerCase()
        if (normalized === 'true' || normalized === '1') return true
        if (normalized === 'false' || normalized === '0') return false
      }
    }
  }
  return undefined
}

/**
 * 读取数值字段。
 *
 * @param records 候选记录集合。
 * @param keys 候选键名集合。
 * @returns 第一个有效数值。
 */
export function readNumberValue(records: Record<string, unknown>[], keys: string[]): number | undefined {
  return readNumberMetadataValue(records, keys)
}

/**
 * 根据 body 条目解析每个 body 对应的可高亮 primitive。
 *
 * @param rootObject body 所在实例根对象。
 * @param bodyEntries body 语义条目。
 * @returns 按 bodyId 聚合的高亮目标。
 */
export function resolveBodyHighlightTargetsById(
  rootObject: THREE.Object3D | undefined,
  bodyEntries: ImportedBodyEntry[] | undefined,
): Record<string, EntityHighlightTarget[]> | undefined {
  if (!rootObject || !bodyEntries || bodyEntries.length === 0) {
    return undefined
  }

  const targetsByBodyId = new Map<string, EntityHighlightTarget[]>()
  rootObject.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) {
      return
    }

    const primitiveSpan = resolveMeshPrimitiveSpan(object)
    const { entries, primitiveIndexOffset } = resolveMeshPrimitiveEntityContext(object)
    if (entries.length === 0) {
      return
    }

    bodyEntries.forEach((bodyEntry) => {
      const bodyEntryLookupKeys = new Set(createImportedBodyOwnershipLookupKeys(bodyEntry.bodyId))
      const localIndices = entries
        .filter((entry) => createImportedBodyOwnershipLookupKeys(entry.bodyId).some((lookupKey) => (
          bodyEntryLookupKeys.has(lookupKey)
        )))
        .map((entry) => entry.primitiveIndex - primitiveIndexOffset)
      const primitiveIndices = normalizeLocalPrimitiveIndices(localIndices, primitiveSpan)
      if (!primitiveIndices) {
        return
      }

      const targets = targetsByBodyId.get(bodyEntry.bodyId) ?? []
      targets.push({
        objectId: object.uuid,
        primitiveIndices,
      })
      targetsByBodyId.set(bodyEntry.bodyId, targets)
    })
  })

  if (targetsByBodyId.size === 0) {
    return undefined
  }

  const result: Record<string, EntityHighlightTarget[]> = {}
  targetsByBodyId.forEach((targets, bodyId) => {
    result[bodyId] = targets
  })
  return result
}

/**
 * 解析 Object3D 上所有可能承载装配语义的 userData 记录。
 *
 * @param object 场景对象。
 * @returns 从外到内的语义记录集合。
 */
export function resolveNodeRecords(object: THREE.Object3D): Record<string, unknown>[] {
  const rootRecord = asRecord(object.userData) ?? {}
  const nested = [
    asRecord(rootRecord.metadata),
    asRecord(rootRecord.component),
    asRecord(rootRecord.componentInfo),
    asRecord(rootRecord.solidworks),
    asRecord(rootRecord.sw),
  ].filter((item): item is Record<string, unknown> => Boolean(item))
  return [rootRecord, ...nested]
}

/**
 * 转换 Three.js 矩阵为可序列化元组。
 *
 * @param matrix Three.js 矩阵。
 * @returns 16 元素矩阵元组。
 */
export function toMatrixTuple(matrix: THREE.Matrix4): Matrix4Tuple {
  const elements = matrix.elements as number[]
  return [
    elements[0], elements[1], elements[2], elements[3],
    elements[4], elements[5], elements[6], elements[7],
    elements[8], elements[9], elements[10], elements[11],
    elements[12], elements[13], elements[14], elements[15],
  ]
}

/**
 * 构建装配 manifest 节点 ID。
 *
 * @param parentNodeId 父节点 ID。
 * @param object 场景对象。
 * @param siblingIndex 同级索引。
 * @returns 稳定节点 ID。
 */
export function buildNodeId(parentNodeId: string | null, object: THREE.Object3D, siblingIndex: number): string {
  const explicitId = typeof object.userData?.nodeId === 'string' ? object.userData.nodeId.trim() : ''
  if (explicitId) {
    return explicitId
  }
  const name = object.name?.trim() || object.type
  return parentNodeId ? `${parentNodeId}/${name}[${siblingIndex}]` : `${name}[${siblingIndex}]`
}

/**
 * 规范化后端导出的外部 GLB 文件下载 ID。
 *
 * 设计说明：
 * 1. 当存在 `external_file_id` 时，GetFile.file_id 由项目 ID 与该值拼接生成，不再使用 `file_name` 等展示字段兜底；
 * 2. 这里仍剥离明确的 `.glb|uuid` / `.glb_uuid` 结尾，避免后端路径带导出防冲突后缀时无法命中；
 * 3. `external_file_id` 不是必填，缺失时上层仍可使用 `external_file_uuid` 通过 GetFile 定位。
 *
 * @param fileId 原始外部文件 ID。
 * @returns 可供 web-client 拼接项目 ID 的外部文件 ID。
 */
function normalizeExternalFileId(fileId: string | undefined): string | undefined {
  const normalizedFileId = fileId?.trim()
  if (!normalizedFileId) {
    return undefined
  }
  return normalizedFileId.replace(EXPORTED_GLB_REFERENCE_SUFFIX_RE, '$1')
}

/**
 * 解析节点上的外部零件引用。
 *
 * @param records 节点语义记录集合。
 * @returns 外部文件引用。
 */
export function resolvePartReference(records: Record<string, unknown>[]): { fileUuid?: string; externalFileId?: string } {
  const rawExternalFileId = readStringValue(records, [
    'external_file_id',
    'externalFileId',
  ])

  return {
    fileUuid: readStringValue(records, ['external_file_uuid', 'externalFileUuid']),
    externalFileId: normalizeExternalFileId(rawExternalFileId),
  }
}

/**
 * 规范化引用字符串。
 *
 * @param value 原始引用。
 * @returns 去空白后的引用。
 */
function normalizeReferenceValue(value: string | undefined): string {
  return value?.trim() ?? ''
}

/**
 * 清理指向当前装配文件自身的伪外部引用。
 *
 * @param partReference 外部零件引用。
 * @param file 当前装配文件。
 * @returns 安全的外部引用。
 */
export function sanitizePartReference(
  partReference: { fileUuid?: string; externalFileId?: string },
  file: FileInfo,
): { fileUuid?: string; externalFileId?: string } {
  const normalizedFileUuid = normalizeReferenceValue(partReference.fileUuid)
  const normalizedFileId = normalizeReferenceValue(partReference.externalFileId)
  const isSelfUuid = normalizedFileUuid.length > 0 && normalizedFileUuid === file.uuid
  const isSelfFileId = normalizedFileId.length > 0 && normalizedFileId === file.fileId

  return {
    fileUuid: isSelfUuid ? undefined : partReference.fileUuid,
    externalFileId: isSelfFileId ? undefined : partReference.externalFileId,
  }
}

/**
 * 解析节点 PMI 元数据。
 *
 * @param records 节点语义记录集合。
 * @returns PMI 元数据。
 */
export function resolveNodePmi(records: Record<string, unknown>[]): LoaderMetadata | undefined {
  for (const record of records) {
    const pmi = cloneMetadata(record.pmi)
    if (pmi) {
      return pmi
    }
  }
  return undefined
}

/**
 * 解析节点 STEP 元数据。
 *
 * @param records 节点语义记录集合。
 * @returns STEP 元数据。
 */
export function resolveNodeStepMetadata(records: Record<string, unknown>[]): LoaderMetadata | undefined {
  for (const record of records) {
    const step = cloneMetadata(record.step)
    if (step) {
      return step
    }
  }
  return undefined
}

/**
 * 解析节点语义类型。
 *
 * @param records 节点语义记录集合。
 * @returns assembly、part 或 unknown。
 */
export function resolveNodeSemanticKind(records: Record<string, unknown>[]): Extract<ImportedSemanticKind, 'assembly' | 'part' | 'unknown'> {
  const step = resolveNodeStepMetadata(records)
  const value = typeof step?.objectType === 'string'
    ? step.objectType
    : readStringValue(records, [
        // Puppy CAD 内核 CAD 当前将装配/零件语义直接写在 extras 根上，
        // 不能只依赖 extras.step.objectType，否则外链 occurrence 会被误判为 unknown。
        'objectType',
        'source_entity_type',
        'sourceEntityType',
        'semanticKind',
        'kind',
        'nodeKind',
        'type',
      ])
  const normalizedValue = value?.trim().toLowerCase()
  if (normalizedValue === 'assembly' || normalizedValue === 'part') {
    return normalizedValue
  }
  return 'unknown'
}

/**
 * 判断对象是否承载实际几何。
 *
 * @param object 场景对象。
 * @returns 是否为几何对象。
 */
export function isGeometryObject(object: THREE.Object3D): boolean {
  return object instanceof THREE.Mesh || object instanceof THREE.LineSegments || object instanceof THREE.Line
}

/**
 * 判断对象是否为语义组件根。
 *
 * @param object 场景对象。
 * @returns 是否为 assembly/part 语义组件。
 */
function isSemanticComponentObject(object: THREE.Object3D): boolean {
  const semanticKind = resolveNodeSemanticKind(resolveNodeRecords(object))
  return semanticKind === 'assembly' || semanticKind === 'part'
}

/**
 * 判断装配节点是否携带内联本地几何。
 *
 * @param object 场景对象。
 * @param isRoot 当前对象是否为检测根。
 * @returns 是否存在本地几何。
 */
export function hasInlineAssemblyLocalGeometry(object: THREE.Object3D, isRoot = true): boolean {
  if (isGeometryObject(object)) {
    return true
  }
  if (!isRoot && isSemanticComponentObject(object)) {
    return false
  }
  return object.children.some((child) => hasInlineAssemblyLocalGeometry(child, false))
}

/**
 * 浅克隆对象并继承被剥离根节点上的关键 userData。
 *
 * @param source 源对象。
 * @returns 克隆对象。
 */
function cloneShallowObject<T extends THREE.Object3D>(source: T): T {
  const originalUserData = source.userData
  source.userData = { ...source.userData }
  const cloned = source.clone(false) as T
  source.userData = originalUserData
  ImportedModelObjectMetadataService.copyObjectMetadata(source, cloned)
  inheritStrippedRootUserData(cloned, [source])
  return cloned
}

/**
 * 克隆装配内联本地几何，遇到嵌套组件根时停止递归。
 *
 * @param source 源对象。
 * @param isRoot 当前对象是否为克隆根。
 * @returns 克隆后的本地几何子树。
 */
export function cloneInlineAssemblyLocalGeometry(source: THREE.Object3D, isRoot = true): THREE.Object3D | null {
  if (!isRoot && isSemanticComponentObject(source)) {
    return null
  }
  if (isGeometryObject(source)) {
    return cloneShallowObject(source)
  }
  const cloned = cloneShallowObject(source)
  source.children.forEach((child) => {
    const childClone = cloneInlineAssemblyLocalGeometry(child, false)
    if (childClone) {
      cloned.add(childClone)
    }
  })
  return cloned.children.length > 0 || isRoot ? cloned : null
}

/**
 * 判断对象是否应进入装配 manifest。
 *
 * @param object 场景对象。
 * @param records 节点语义记录集合。
 * @returns 是否写入 manifest。
 */
export function shouldIncludeManifestNode(object: THREE.Object3D, records: Record<string, unknown>[]): boolean {
  const explicitSelectable = readBooleanValue(records, ['selectable', 'isSelectable'])
  const semanticKind = resolveNodeSemanticKind(records)
  if (explicitSelectable === true || semanticKind !== 'unknown') {
    return true
  }
  if (isGeometryObject(object)) {
    const partRef = resolvePartReference(records)
    return Boolean(partRef.fileUuid)
  }
  if (object.parent === null) {
    return true
  }
  const partRef = resolvePartReference(records)
  return Boolean(partRef.fileUuid)
}

/**
 * 判断节点是否携带文件自身身份元数据。
 *
 * 设计说明：
 * 1. `fileUuid/fileId` 描述当前 GLB 文件自身，不能作为 external_file_uuid 外部引用处理；
 * 2. 根包装裁剪仍需要尊重这类元数据，否则会把真实根节点剥掉，导致子外链节点变成 manifest 根；
 * 3. 该判断只用于根包装保留，不参与外部引用加载链路。
 *
 * @param records 节点语义记录集合。
 * @returns 是否存在文件自身身份字段。
 */
function hasFileIdentityMetadata(records: Record<string, unknown>[]): boolean {
  return Boolean(readStringValue(records, ['file_uuid', 'fileUuid', 'file_id', 'fileId']))
}

/**
 * 判断是否应剥离装配根外层包装节点。
 *
 * @param object 候选对象。
 * @returns 是否应剥离。
 */
function shouldStripAssemblyRootWrapper(object: THREE.Object3D): boolean {
  if (object.children.length !== 1) {
    return false
  }

  const records = resolveNodeRecords(object)
  const partReference = resolvePartReference(records)
  if (partReference.fileUuid) {
    return false
  }
  if (hasFileIdentityMetadata(records)) {
    return false
  }
  if (resolveNodeSemanticKind(records) !== 'unknown') {
    return false
  }
  if (resolveNodePmi(records)) {
    return false
  }
  return !isGeometryObject(object)
}

/**
 * 规范化装配根节点，剥离无语义外层包装。
 *
 * @param root GLTF scene 根节点。
 * @returns 规范根、剥离数量和剥离节点列表。
 */
export function resolveCanonicalAssemblyRoot(root: THREE.Object3D): {
  canonicalRoot: THREE.Object3D
  strippedNodeCount: number
  strippedWrappers: THREE.Object3D[]
} {
  let canonicalRoot = root
  const strippedWrappers: THREE.Object3D[] = []
  while (shouldStripAssemblyRootWrapper(canonicalRoot)) {
    strippedWrappers.push(canonicalRoot)
    canonicalRoot = canonicalRoot.children[0]!
  }
  return {
    canonicalRoot,
    strippedNodeCount: strippedWrappers.length,
    strippedWrappers,
  }
}
